import {
  ChakraFlex,
  ChakraGridItem,
  ChakraSimpleGrid,
  DateFormat,
  FAIconName,
  TagColor,
  TitleAndValueV2,
} from '@equip.health/ui';
import { DateTime } from 'luxon';
import {
  getAddressDetails,
  getPrimaryDetails,
  getSecondaryDetails,
} from '~/lib/util/patient.util';
import SurveyDataWarning from '~/components/common/SurveyDataWarning';

interface PatientBasicInfoProps {
  basicInfo: Patient;
  displayInformationBox?: boolean;
  onSurveyButtonClick?: () => void;
}

export type Field = {
  leftIcon?: FAIconName;
  value: string;
  rightIcon?: FAIconName;
  isBorderRequired?: boolean;
  isTag?: boolean;
  color?: TagColor;
};

export type ValueObject = {
  isFlexWrapAllowed?: boolean;
  fields: Field[];
};

export type TitleAndValuePropsV2 = {
  id: string;
  isRequired?: boolean;
  title: string;
  value: string | ValueObject;
};

export const renderField = (field: TitleAndValuePropsV2) => {
  return (
    <TitleAndValueV2
      id={field.id}
      isRequired={field.isRequired}
      key={field.id}
      title={field.title}
      value={field.value}
    />
  );
};

const ViewBasicInfo = ({
  basicInfo,
  displayInformationBox = false,
  onSurveyButtonClick,
}: PatientBasicInfoProps) => {
  const {
    addressLine1,
    addressLine2,
    affiliationName,
    chosenName,
    city,
    contactPreferences,
    country,
    county,
    dateOfBirth,
    email,
    ethnicities,
    faxNumber,
    firstName,
    genderOffRecord,
    genderOnRecord,
    hasGivenRecordingConsent,
    homePhone,
    languages,
    lastName,
    mrn,
    officeNumber,
    patientTags,
    phoneNumber,
    pronounsOffRecord,
    pronounsOnRecord,
    secondaryPhoneNumber,
    sex,
    shippingAddress,
    state,
    timezone,
    zip,
    isAccountActive,
  } = basicInfo;

  const formattedDob = dateOfBirth
    ? DateTime.fromSQL(dateOfBirth).toFormat(DateFormat.longDate)
    : '';

  const mainAddress: PatientAddress = {
    addressLine1,
    addressLine2,
    affiliationName,
    city,
    country,
    county,
    state,
    zip,
  };

  const primaryDetails = getPrimaryDetails({
    chosenName,
    contactPreferences,
    email,
    faxNumber,
    firstName,
    formattedDob,
    isAccountActive,
    homePhone,
    lastName,
    mrn,
    officeNumber,
    phoneNumber,
    secondaryPhoneNumber,
  });

  const secondaryDetails = getSecondaryDetails({
    ethnicities,
    genderOffRecord,
    genderOnRecord,
    hasGivenRecordingConsent,
    languages,
    patientTags,
    pronounsOffRecord,
    pronounsOnRecord,
    sex,
  });

  const addressDetails = getAddressDetails(
    mainAddress,
    shippingAddress,
    timezone,
  );

  return (
    <>
      {displayInformationBox && (
        <SurveyDataWarning isEditMode={false} onReview={onSurveyButtonClick} />
      )}

      <ChakraSimpleGrid
        columns={3}
        margin="18px 0px 24px 0px"
        padding="0px 8px"
        spacing="36px"
      >
        <ChakraGridItem colSpan={1}>
          <ChakraFlex flexDirection="column" gap="18px" minHeight="300px">
            {primaryDetails.map(renderField)}
          </ChakraFlex>
        </ChakraGridItem>
        <ChakraGridItem colSpan={1}>
          <ChakraFlex flexDirection="column" gap="18px" minHeight="300px">
            {addressDetails.map(renderField)}
          </ChakraFlex>
        </ChakraGridItem>
        <ChakraGridItem colSpan={1}>
          <ChakraFlex flexDirection="column" gap="18px" minHeight="300px">
            {secondaryDetails.map(renderField)}
          </ChakraFlex>
        </ChakraGridItem>
      </ChakraSimpleGrid>
    </>
  );
};

export default ViewBasicInfo;
